<script setup lang="ts">
import { ref, shallowRef, watchEffect } from 'vue';
import DashboardPanel from '@/modules/dashboard/components/DashboardPanel.vue';
import BrokerCompanySelector from '@/modules/broker-admin/views/BrokerCompanySelector.vue';
import { CompanyInfo } from '@/modules/common/models';
import { reportingService } from '@/connect/services/reporting';
import { CompanyReportItem } from '@/connect/gen/modules/apiengine/services/reporting/reporting_pb';
import { Data } from '@/connect';
import { AgTableClient } from '@/modules/common/components/ag-table';
import { ColDef } from 'ag-grid-enterprise';
import { formatAnyDate } from '@/utils/helpers/dates';
import {
  alignCenter,
  alignLeft,
  mergeClasses,
} from '@/modules/common/components/ag-table/columns/utils';

const selectedCompany = shallowRef<CompanyInfo | null>(null);
const reports = ref<Array<Data<CompanyReportItem>>>([]);
const loading = ref(false);

watchEffect(async () => {
  const company = selectedCompany.value;
  if (!company) {
    reports.value = [];
    return;
  }

  loading.value = true;
  try {
    const result = await reportingService.listCompanyReports(company.companyId);
    reports.value = result.success ? result.data.reports : [];
  } catch {
    reports.value = [];
  } finally {
    loading.value = false;
  }
});

type ReportRow = Data<CompanyReportItem>;

const columnDefs: Array<ColDef<ReportRow>> = [
  {
    colId: 'reportName',
    headerName: 'Report Name',
    field: 'reportName',
    ...alignLeft(),
  },
  {
    colId: 'lastDownloadTime',
    headerName: 'Last Downloaded',
    field: 'lastDownloadTime',
    valueFormatter: (params) => {
      if (!params.value) return '-';
      return formatAnyDate(params.value, 'yyyy-MM-dd HH:mm');
    },
    ...alignLeft(),
  },
  {
    colId: 'lastDeliveryTime',
    headerName: 'Last Delivered',
    field: 'lastDeliveryTime',
    valueFormatter: (params) => {
      if (!params.value) return '-';
      return formatAnyDate(params.value, 'yyyy-MM-dd HH:mm');
    },
    cellClass: (params) =>
      (params.value ? alignLeft().cellClass : alignCenter().cellClass) as string | string[],
  },
  {
    colId: 'lastDeliverySuccess',
    headerName: 'Status',
    field: 'lastDeliverySuccess',
    cellRenderer: (params: { value: boolean; data: ReportRow }) => {
      if (params.data.lastDeliveryTime === undefined) return '-';
      return params.value ? '✓' : '✗';
    },
    ...mergeClasses([
      alignCenter(),
      {
        cellClass: (params) => {
          if (params.data?.lastDeliveryTime === undefined) return '';
          return params.value ? 'success--text' : 'error--text';
        },
      },
    ]),
  },
  {
    colId: 'recurring',
    headerName: 'Recurring',
    field: 'recurring',
    cellRenderer: (params: { value: boolean }) => {
      return params.value ? '✓' : '✗';
    },
    ...mergeClasses([
      alignCenter(),
      {
        cellClass: (params) => (params.value ? 'success--text' : ''),
      },
    ]),
  },
];
</script>

<template>
  <div>
    <div class="d-flex align-center justify-space-between mb-4">
      <BrokerCompanySelector v-model="selectedCompany" autofocus class="company-selector" />
    </div>
    <DashboardPanel no-collapse title="Report Delivery">
      <ag-table-client
        auto-grow
        :column-defs="columnDefs"
        :get-row-id="(r) => r.reportType"
        :loading="loading"
        :row-data="reports"
        table-id="BrokerClientReporting"
      />
    </DashboardPanel>
  </div>
</template>

<style lang="scss">
.company-selector {
  width: 325px;
}
</style>
